import { useState } from 'react';

const CATEGORIES = ['Clothing', 'Shoes', 'Beauty', 'Electronics', 'Home', 'Accessories', 'Food', 'Other'];

const CATEGORY_EMOJI = {
  Clothing: '👕', Shoes: '👟', Beauty: '✨', Electronics: '📱',
  Home: '🏡', Accessories: '👜', Food: '☕', Other: '✦',
};

const DURATION_OPTIONS = [
  { value: 1,  label: '1 Day',   subtitle: 'Quick breath'     },
  { value: 3,  label: '3 Days',  subtitle: 'Recommended'      },
  { value: 7,  label: '7 Days',  subtitle: 'Deep reflection'  },
  { value: 14, label: '14 Days', subtitle: 'True patience'    },
  { value: 30, label: '30 Days', subtitle: 'The big wait'     },
];

export default function AddItemForm({ onAdd, onCancel }) {
  const [step, setStep]         = useState('url');
  const [url, setUrl]           = useState('');
  const [urlError, setUrlError] = useState('');
  const [product, setProduct]   = useState(null);

  // URL-step fields collected upfront
  const [optionalName,  setOptionalName]  = useState('');
  const [selectedDays,  setSelectedDays]  = useState(3);
  const [reflection,    setReflection]    = useState('');

  // Preview-step editable fields
  const [editName,     setEditName]     = useState('');
  const [editPrice,    setEditPrice]    = useState('');
  const [editCategory, setEditCategory] = useState('Other');
  const [partialMsg,   setPartialMsg]   = useState('');

  // Manual fallback
  const [manualName,  setManualName]  = useState('');
  const [manualPrice, setManualPrice] = useState('');

  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) { setUrlError('Paste a product URL to continue'); return; }
    try { new URL(trimmed); } catch { setUrlError("That doesn't look like a valid URL"); return; }

    setStep('loading');

    try {
      const res  = await fetch(`/api/fetch-url?url=${encodeURIComponent(trimmed)}`);
      const data = await res.json();

      if (!res.ok || data.error) throw new Error(data.message || 'fetch failed');

      setProduct(data);
      setEditName(optionalName.trim() || data.name || '');
      setEditPrice(data.price !== null && data.price !== undefined ? String(data.price) : '');
      setEditCategory(data.category || 'Other');
      setPartialMsg(data.partial ? (data.partialMessage || '') : '');
      setStep('preview');
    } catch {
      setStep('error');
    }
  };

  const handleConfirm = () => {
    onAdd({
      url,
      name:       editName.trim(),
      price:      editPrice,
      image:      product?.image || null,
      category:   editCategory,
      siteName:   product?.siteName || null,
      simmerDays: selectedDays,
    });
  };

  const goManual = () => {
    setManualName(optionalName.trim());
    setManualPrice('');
    setStep('manual');
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    onAdd({
      url,
      name:       manualName.trim(),
      price:      manualPrice,
      image:      null,
      category:   'Other',
      siteName:   null,
      simmerDays: selectedDays,
    });
  };

  // ── URL step — editorial full-page form ───────────────────────────────────
  if (step === 'url') return (
    <div className="aef-shell">
      <div className="aef-orb aef-orb--1" />
      <div className="aef-orb aef-orb--2" />

      <div className="aef-inner">
        <button className="aef-close" onClick={onCancel} aria-label="Close">
          <span className="material-symbols-outlined">close</span>
        </button>

        <div className="aef-header">
          <h1 className="aef-headline">Add to your Simmer</h1>
          <p className="aef-subhead">
            Give your desire a moment to settle. Place the item in the pot and let the impulse fade into clarity.
          </p>
        </div>

        <div className="aef-card">
          <form className="aef-form" onSubmit={handleUrlSubmit} noValidate>

            {/* URL — primary field */}
            <div>
              <label className="aef-label-headline" htmlFor="aef-url">The product link</label>
              <input
                id="aef-url"
                type="url"
                className="aef-url-input"
                placeholder="https://..."
                value={url}
                onChange={e => { setUrl(e.target.value); setUrlError(''); }}
                autoFocus
                required
              />
              {urlError && <p className="aef-error-text">{urlError}</p>}
            </div>

            {/* Two-column grid: optional name + duration */}
            <div className="aef-grid">
              <div>
                <label className="aef-label-caps" htmlFor="aef-name">Optional: Product Name</label>
                <input
                  id="aef-name"
                  type="text"
                  className="aef-input-minimal"
                  placeholder="What is it called?"
                  value={optionalName}
                  onChange={e => setOptionalName(e.target.value)}
                />
              </div>
              <div>
                <label className="aef-label-caps" htmlFor="aef-duration">Simmer Duration</label>
                <div className="aef-select-wrap">
                  <select
                    id="aef-duration"
                    className="aef-select"
                    value={selectedDays}
                    onChange={e => setSelectedDays(Number(e.target.value))}
                  >
                    {DURATION_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} ({opt.subtitle})
                      </option>
                    ))}
                  </select>
                  <span className="material-symbols-outlined aef-select-icon">expand_more</span>
                </div>
              </div>
            </div>

            {/* Reflection textarea */}
            <div>
              <label className="aef-label-caps" htmlFor="aef-reflection">Why do you want this?</label>
              <textarea
                id="aef-reflection"
                className="aef-reflection"
                placeholder="Is this a need, or a passing whisper of a want?"
                rows={5}
                value={reflection}
                onChange={e => setReflection(e.target.value)}
              />
            </div>

            {/* CTA */}
            <div className="aef-cta">
              <button type="submit" className="aef-submit">Start Simmering</button>
              <p className="aef-cta-note">You can change your mind at any time.</p>
            </div>

          </form>
        </div>
      </div>
    </div>
  );

  // ── Loading step ──────────────────────────────────────────────────────────
  if (step === 'loading') return (
    <div className="aef-shell">
      <div className="aef-orb aef-orb--1" />
      <div className="aef-orb aef-orb--2" />
      <div className="aef-inner aef-inner--centered">
        <div className="af-loading">
          <div className="af-loading-icon">🔥</div>
          <p className="af-loading-text">Simmering your link…</p>
          <p className="af-loading-sub">Fetching product details</p>
        </div>
      </div>
    </div>
  );

  // ── Preview step ──────────────────────────────────────────────────────────
  if (step === 'preview') return (
    <div className="aef-shell">
      <div className="aef-orb aef-orb--1" />
      <div className="aef-orb aef-orb--2" />
      <div className="aef-inner">
        <button className="aef-close" onClick={onCancel} aria-label="Close">
          <span className="material-symbols-outlined">close</span>
        </button>

        <div className="aef-header">
          <h1 className="aef-headline">Looks right?</h1>
          <p className="aef-subhead">Confirm the details before it goes into the pot.</p>
        </div>

        <div className="aef-card">
          {product?.siteName && (
            <p className="af-site-name">{product.siteName}</p>
          )}
          {partialMsg && (
            <p className="af-partial-msg">{partialMsg}</p>
          )}
          {product?.image && (
            <div className="af-image-wrap">
              <img
                src={product.image}
                alt={editName}
                className={`af-product-image${product.image.includes('logo.clearbit.com') ? ' af-product-image--logo' : ''}`}
                onError={e => { e.target.style.display = 'none'; }}
              />
            </div>
          )}

          <div className="aef-form" style={{ marginTop: product?.image ? '20px' : 0 }}>
            <div>
              <label className="aef-label-caps" htmlFor="pv-name">Product name</label>
              <input
                id="pv-name"
                type="text"
                className="aef-input-minimal"
                value={editName}
                onChange={e => setEditName(e.target.value)}
              />
            </div>

            <div>
              <label className="aef-label-caps" htmlFor="pv-price">Price</label>
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  id="pv-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={editPrice}
                  onChange={e => setEditPrice(e.target.value)}
                />
              </div>
              {editPrice === '' && (
                <span className="field-hint">We couldn't find the price — add it manually</span>
              )}
            </div>

            <div>
              <label className="aef-label-caps">Category</label>
              <div className="category-pills" style={{ marginTop: '8px' }}>
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    className={`cat-pill ${editCategory === cat ? 'cat-pill--active' : ''}`}
                    onClick={() => setEditCategory(cat)}
                  >
                    {CATEGORY_EMOJI[cat]} {cat}
                  </button>
                ))}
              </div>
            </div>

            <div className="aef-cta">
              <button type="button" className="aef-submit" onClick={handleConfirm}>
                Add to Simmer
              </button>
              <button type="button" className="btn-secondary" onClick={goManual}>
                Edit manually
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Error step ────────────────────────────────────────────────────────────
  if (step === 'error') return (
    <div className="aef-shell">
      <div className="aef-orb aef-orb--1" />
      <div className="aef-orb aef-orb--2" />
      <div className="aef-inner">
        <button className="aef-close" onClick={onCancel} aria-label="Close">
          <span className="material-symbols-outlined">close</span>
        </button>

        <div className="aef-header">
          <h1 className="aef-headline">Hmm, couldn't read that</h1>
          <p className="aef-subhead">
            Some sites block automated requests. Want to fill in the details yourself?
          </p>
        </div>

        <div className="aef-card">
          <div className="aef-cta">
            <button type="button" className="aef-submit" onClick={goManual}>
              Fill in myself →
            </button>
            <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Manual fallback step ──────────────────────────────────────────────────
  return (
    <div className="aef-shell">
      <div className="aef-orb aef-orb--1" />
      <div className="aef-orb aef-orb--2" />
      <div className="aef-inner">
        <button className="aef-close" onClick={onCancel} aria-label="Close">
          <span className="material-symbols-outlined">close</span>
        </button>

        <div className="aef-header">
          <h1 className="aef-headline">Add manually</h1>
          <p className="aef-subhead">Fill in what you know — price and name help the most.</p>
        </div>

        <div className="aef-card">
          <form className="aef-form" onSubmit={handleManualSubmit}>
            <div>
              <label className="aef-label-caps">URL</label>
              <input type="url" className="aef-input-minimal field-readonly" value={url} readOnly />
            </div>
            <div>
              <label className="aef-label-caps" htmlFor="mn-name">Product name</label>
              <input
                id="mn-name"
                type="text"
                className="aef-input-minimal"
                placeholder="e.g. Nike Air Max 90"
                value={manualName}
                onChange={e => setManualName(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="aef-label-caps" htmlFor="mn-price">Price</label>
              <div className="price-input">
                <span className="currency">$</span>
                <input
                  id="mn-price"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={manualPrice}
                  onChange={e => setManualPrice(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="aef-label-caps" htmlFor="mn-duration">Simmer Duration</label>
              <div className="aef-select-wrap">
                <select
                  id="mn-duration"
                  className="aef-select"
                  value={selectedDays}
                  onChange={e => setSelectedDays(Number(e.target.value))}
                >
                  {DURATION_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} ({opt.subtitle})
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined aef-select-icon">expand_more</span>
              </div>
            </div>
            <div className="aef-cta">
              <button type="submit" className="aef-submit">Add to Simmer</button>
              <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
